/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useSelector } from 'react-redux';
import type { CsrAccount } from 'sarsaparilla/auth';
import type { AuthAccount, AuthSliceState } from '../redux/auth.slice';

export interface AuthState {
    /**
     * The nested profile, or `null`. Note this is still `null` on the first
     * render of a signed-in session (pre-hydrate), even when `isRegisteredUser`
     * is already `true` — the flags reflect the session wrapper, not the profile.
     */
    account: AuthAccount | null;
    /** The signed-in CSR, or `null` outside an external CSR session. Independent of `account`. */
    csrAccount: CsrAccount | null;
    isCommercial: boolean;
    isGuest: boolean;
    isLoggedOut: boolean;
    isRegisteredUser: boolean;
    isRegisteredUserOrGuest: boolean;
}

export function useAuthState(): AuthState {
    // Select the narrowest fields so consumers only re-render when they change,
    // not on every unrelated `auth` write (errorText, loginError…).
    const account = useSelector(
        (state: { auth: AuthSliceState }) => state.auth?.account ?? null
    );
    const csrAccount = useSelector(
        (state: { auth: AuthSliceState }) => state.auth?.csrAccount ?? null
    );

    const isGuest = !!account && account.is_guest === true;
    // `!== true` to provide a bit of slack for tests or API bugs
    const isRegisteredUser = !!account && account.is_guest !== true;

    return {
        account: account?.account ?? null,
        csrAccount,
        isCommercial: account?.account?.is_commercial_account === true,
        isGuest,
        isLoggedOut: !account,
        isRegisteredUser,
        isRegisteredUserOrGuest: isGuest || isRegisteredUser,
    };
}
